import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Beaker as BeakerIcon } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Preparing Sodium Hydroxide',
    content: 'Sodium hydroxide (NaOH) is a strong base that dissociates completely in water:\n\nNaOH -> Na+ + OH-\n\nWe use 30 mL of 3 M NaOH to provide excess hydroxide ions for the precipitation reaction.',
    whatHappens: 'The NaOH solution is measured in a graduated cylinder. This concentrated base will provide the OH- ions needed to precipitate copper(II) hydroxide.',
    observe: [
      'Clear, colorless NaOH solution',
      'Measure approximately 30 mL',
      'Handle with care - caustic substance',
      'Solution may feel slippery if contacted'
    ]
  },
  2: {
    title: 'Precipitation of Cu(OH)₂',
    content: 'This is a double displacement (precipitation) reaction:\n\nCu(NO3)2(aq) + 2NaOH(aq) -> Cu(OH)₂(s) + 2NaNO3(aq)\n\nCopper(II) hydroxide is insoluble in water (Ksp = 2.2 x 10^-20), so it precipitates as a gelatinous blue solid.',
    whatHappens: 'When NaOH is added dropwise while stirring, Cu²⁺ ions combine with OH- ions to form insoluble Cu(OH)₂. The blue precipitate forms throughout the solution.',
    observe: [
      'Blue gelatinous precipitate forms immediately',
      'Color changes from blue-green solution to thick blue suspension',
      'Precipitate has fluffy, gel-like appearance',
      'Stirring helps distribute the precipitate evenly'
    ]
  }
};

// Animated precipitate forming
function PrecipitateAnimation({ active, progress }: { active: boolean; progress: number }) {
  if (!active || progress === 0) return null;

  const particles = Math.floor(progress * 15);

  return (
    <g>
      {[...Array(particles)].map((_, i) => (
        <circle
          key={i}
          cx={35 + (i % 5) * 15 + Math.random() * 5}
          cy={80 + Math.random() * 40}
          r={2 + Math.random() * 2}
          fill={copperCycleColors.cuOH2Precipitate}
          opacity={0.7}
        >
          <animate
            attributeName="cy"
            values={`${50 + i * 3};${120 - i * 2}`}
            dur={`${2 + i * 0.1}s`}
            fill="freeze"
          />
        </circle>
      ))}
    </g>
  );
}

export default function Part2Precipitation() {
  const [naohPrepared, setNaohPrepared] = useState(false);
  const [addingNaoh, setAddingNaoh] = useState(false);
  const [precipitateForming, setPrecipitateForming] = useState(false);
  const [precipitateComplete, setPrecipitateComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [precipitateProgress, setPrecipitateProgress] = useState(0);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [naohPrepared, precipitateComplete].filter(Boolean).length;

  const handlePrepareNaOH = useCallback(() => {
    if (!naohPrepared) {
      setNaohPrepared(true);
      setCurrentStep(2);
    }
  }, [naohPrepared]);

  const handleAddNaOH = useCallback(() => {
    if (naohPrepared && !addingNaoh) {
      setAddingNaoh(true);
      setPrecipitateForming(true);

      // Animate precipitate formation
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.1;
        setPrecipitateProgress(progress);
        if (progress >= 1) {
          clearInterval(interval);
          setPrecipitateComplete(true);
          setAddingNaoh(false);
        }
      }, 300);
    }
  }, [naohPrepared, addingNaoh]);

  const getSolutionColor = () => {
    if (precipitateComplete) return copperCycleColors.cuOH2Precipitate;
    if (precipitateForming) return copperCycleColors.reaction2Mid;
    return copperCycleColors.cuNO32Solution;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part IV: Cu(NO3)2 + NaOH</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare NaOH', hint: 'Measure 30 mL of 3 M NaOH', done: naohPrepared },
            { id: 2, title: 'Add NaOH Slowly', hint: 'Add dropwise while stirring', done: precipitateComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Color indicator */}
        <div className="color-indicator">
          <span className="label">Solution Color:</span>
          <div className="color-swatch" style={{ background: getSolutionColor() }} />
          <span className="color-name">
            {precipitateComplete ? 'Blue Cu(OH)₂' : precipitateForming ? 'Transitioning...' : 'Blue-green Cu(NO3)2'}
          </span>
        </div>
      </div>

      <LabBench
        experimentName="Precipitation of Cu(OH)₂"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['Beaker with Cu(NO3)2', '3 M NaOH', 'Stirring rod']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* NaOH Cylinder */}
            <div
              className={`equipment-station ${currentStep === 1 && !naohPrepared ? 'clickable' : ''}`}
              onClick={handlePrepareNaOH}
              style={{ cursor: currentStep === 1 && !naohPrepared ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 50 100" width={50} height={100}>
                <defs>
                  <linearGradient id={`cylinder-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                  </linearGradient>
                </defs>
                <rect x="12" y="15" width="26" height="70" fill={`url(#cylinder-${id})`} stroke="#7A9BB8" strokeWidth="1.5" rx="2" />
                <ellipse cx="25" cy="15" rx="13" ry="4" fill="rgba(200,220,240,0.5)" stroke="#7A9BB8" strokeWidth="1" />
                {naohPrepared && (
                  <rect x="14" y="40" width="22" height="43" fill="rgba(255,255,255,0.9)" rx="1" />
                )}
                <text x="25" y="95" fontSize="8" fill="#E0E0E0" textAnchor="middle">3M NaOH</text>
              </svg>
              <span className="equipment-label">30 mL NaOH</span>
            </div>

            {/* Reaction Beaker */}
            <div
              className={`equipment-station ${currentStep === 2 && !precipitateComplete ? 'clickable' : ''}`}
              onClick={handleAddNaOH}
              style={{ cursor: currentStep === 2 && !precipitateComplete ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 140 180" width={160} height={200}>
                <defs>
                  <linearGradient id={`beaker-glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                  <linearGradient id={`solution-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={getSolutionColor()} stopOpacity="0.85" />
                    <stop offset="100%" stopColor={getSolutionColor()} stopOpacity="0.95" />
                  </linearGradient>
                </defs>

                {/* Beaker body */}
                <rect x="25" y="25" width="90" height="120" fill={`url(#beaker-glass-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="2" />

                {/* Solution with transition */}
                <rect x="28" y="55" width="84" height="88" fill={`url(#solution-${id})`}>
                  <animate
                    attributeName="fill"
                    values={precipitateForming && !precipitateComplete ?
                      `${copperCycleColors.cuNO32Solution};${copperCycleColors.reaction2Mid};${copperCycleColors.cuOH2Precipitate}` : undefined}
                    dur="3s"
                    fill="freeze"
                  />
                </rect>

                {/* Precipitate particles */}
                <PrecipitateAnimation active={precipitateForming} progress={precipitateProgress} />

                {/* Gelatinous precipitate settled */}
                {precipitateComplete && (
                  <g>
                    <ellipse cx="70" cy="135" rx="35" ry="8" fill={copperCycleColors.cuOH2Precipitate} opacity="0.9" />
                    <ellipse cx="70" cy="132" rx="32" ry="6" fill="#3CB371" opacity="0.4" />
                  </g>
                )}

                {/* Stirring animation */}
                {addingNaoh && (
                  <g>
                    <line x1="70" y1="30" x2="70" y2="100" stroke="#888" strokeWidth="3">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        values="0 70 100;10 70 100;-10 70 100;0 70 100"
                        dur="0.5s"
                        repeatCount="indefinite"
                      />
                    </line>
                  </g>
                )}

                <text x="70" y="165" fontSize="10" fill="#E0E0E0" textAnchor="middle">Reaction Beaker</text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !naohPrepared && (
              <div className="status-message info">
                <BeakerIcon size={20} />
                Measure 30 mL of 3 M NaOH in a graduated cylinder
              </div>
            )}
            {currentStep === 2 && !addingNaoh && !precipitateComplete && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the beaker to slowly add NaOH while stirring
              </div>
            )}
            {addingNaoh && (
              <div className="status-message reaction">
                <FlaskConical size={20} />
                Blue Cu(OH)₂ precipitate forming...
              </div>
            )}
            {precipitateComplete && (
              <div className="status-message success">
                <Check size={20} />
                Blue gelatinous Cu(OH)₂ precipitate formed!
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Double Displacement Reaction</h4>
            <div className="equation">
              Cu(NO3)2 + 2NaOH → Cu(OH)₂↓ + 2NaNO3
            </div>
            <p>
              The Cu²⁺ ions combine with OH- ions to form insoluble copper(II) hydroxide.
              This blue precipitate has a characteristic gelatinous appearance.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
            text-align: center;
          }

          .color-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 16px;
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .color-indicator .label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .color-swatch {
            width: 24px;
            height: 24px;
            border-radius: 4px;
            border: 1px solid rgba(255,255,255,0.2);
            transition: background 0.5s ease;
          }

          .color-name {
            font-size: 11px;
            color: var(--text-primary);
            font-weight: 500;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.reaction {
            background: rgba(30, 144, 255, 0.3);
            color: #1E90FF;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: #1E90FF;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
